"use client";

import { CSSProperties, FC, MouseEvent, useEffect, useRef, useState } from "react";
import Link from "next/link";
import styles from "./TableOfContent.module.css";

export interface TableOfContentDto {
  textAlign?: "inherit" | "left" | "center" | "right" | "justify";
  color?: string;
  fontSize?: string;
  marginBottom?: string;
  items: TableOfContentItem[];
}

export interface TableOfContentItem {
  id: string;
  text: string;
}

type TocVariant = "rail" | "chips";

interface TableOfContentProps {
  tableOfContent: TableOfContentDto;
  variant?: TocVariant;
}

const HEADER_OFFSET_PX = 72;

function isIntroItem(item: TableOfContentItem) {
  return item.id.toLowerCase() === "intro" || item.text.toLowerCase() === "intro";
}

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function getNavClass(variant: TocVariant): string {
  switch (variant) {
    case "rail":
      return styles.nav;
    case "chips":
      return `${styles.nav} ${styles.chipsNav}`;
    default: {
      const _never: never = variant;
      return _never;
    }
  }
}

function scrollToSection(item: TableOfContentItem) {
  const behavior = prefersReducedMotion() ? "auto" : "smooth";

  if (isIntroItem(item)) {
    window.scrollTo({ top: 0, behavior });
    return;
  }

  const target = document.getElementById(item.id);
  if (!target) {
    return;
  }

  const top = Math.max(
    0,
    window.scrollY + target.getBoundingClientRect().top - HEADER_OFFSET_PX,
  );
  window.scrollTo({ top, behavior });
}

function getActiveSectionId(items: TableOfContentItem[]) {
  const introId = items.find(isIntroItem)?.id ?? items[0]?.id ?? "";
  let currentId = introId;

  for (const item of items) {
    if (isIntroItem(item)) {
      continue;
    }

    const target = document.getElementById(item.id);
    if (!target) {
      continue;
    }

    if (target.getBoundingClientRect().top - HEADER_OFFSET_PX <= 8) {
      currentId = item.id;
    }
  }

  return currentId;
}

function hasArrivedAtSection(item: TableOfContentItem) {
  if (isIntroItem(item)) {
    return window.scrollY <= 8;
  }

  const target = document.getElementById(item.id);
  if (!target) {
    return true;
  }

  return target.getBoundingClientRect().top - HEADER_OFFSET_PX <= 8;
}

const TableOfContent: FC<TableOfContentProps> = ({
  tableOfContent,
  variant = "rail",
}) => {
  const items = tableOfContent.items;
  const [activeId, setActiveId] = useState(items[0]?.id ?? "");
  const [indicator, setIndicator] = useState({ y: 0, h: 0 });
  const pendingItemRef = useRef<TableOfContentItem | null>(null);
  const pendingUntilRef = useRef(0);
  const listRef = useRef<HTMLOListElement>(null);

  useEffect(() => {
    let frame = 0;

    function updateActive() {
      frame = 0;
      const pending = pendingItemRef.current;
      if (pending) {
        const timedOut = performance.now() > pendingUntilRef.current;
        if (!hasArrivedAtSection(pending) && !timedOut) {
          return;
        }
        pendingItemRef.current = null;
      }
      setActiveId(getActiveSectionId(items));
    }

    function onScroll() {
      if (frame) {
        return;
      }
      frame = window.requestAnimationFrame(updateActive);
    }

    updateActive();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [items]);

  useEffect(() => {
    const list = listRef.current;
    if (!list) {
      return;
    }

    const active = list.querySelector<HTMLElement>('[aria-current="location"]');
    const item = active?.closest("li");
    const target = item ?? active;
    if (!target) {
      return;
    }

    setIndicator({ y: target.offsetTop, h: target.offsetHeight });

    if (variant !== "chips" || list.offsetParent === null) {
      return;
    }

    const left = target.offsetLeft - list.clientWidth / 2 + target.offsetWidth / 2;
    list.scrollTo({
      left: Math.max(0, left),
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });
  }, [activeId, variant]);

  const onItemClick = (
    event: MouseEvent<HTMLAnchorElement>,
    item: TableOfContentItem,
  ) => {
    event.preventDefault();
    pendingItemRef.current = item;
    pendingUntilRef.current = performance.now() + 1000;
    setActiveId(item.id);
    scrollToSection(item);
  };

  const titleId =
    variant === "chips" ? "article-toc-title-mobile" : "article-toc-title";

  return (
    <nav className={getNavClass(variant)} aria-labelledby={titleId}>
      <h2 id={titleId} className={styles.title}>
        On this page
      </h2>
      <div
        className={variant === "rail" ? styles.listWrap : undefined}
        style={
          {
            "--toc-y": `${indicator.y}px`,
            "--toc-h": `${indicator.h}px`,
          } as CSSProperties
        }
      >
        {variant === "rail" ? (
          <span className={styles.indicator} aria-hidden="true" />
        ) : null}
        <ol ref={listRef} className={styles.list}>
          {items.map((item) => (
            <li key={item.id}>
              <Link
                href={isIntroItem(item) ? "#" : `#${item.id}`}
                scroll={false}
                onClick={(event) => onItemClick(event, item)}
                className={styles.link}
                aria-current={activeId === item.id ? "location" : undefined}
              >
                {item.text}
              </Link>
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
};

export default TableOfContent;
